import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';
import { setDisplayTimezone } from '../components/pulse/helpers';

// Harmony Pulse API context.
//
// ISOLATION: session keep-alive goes through the shared useAuthRefresh hook
// with apiPrefix '/api/pulse'. That hook scopes its axios interceptors by URL
// — it only rewrites Authorization headers and only recovers from 401s for
// calls whose URL contains that prefix. Calls belonging to any other Harmony
// app pass through untouched, so mounting this provider cannot affect the
// shell or any other sub-app sharing the same axios instance.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const API_PREFIX = '/api/pulse';

const PulseContext = createContext(null);

export const usePulse = () => {
  const ctx = useContext(PulseContext);
  if (!ctx) throw new Error('usePulse must be used inside PulseProvider');
  return ctx;
};

export const PulseProvider = ({ children, token, role, userName, userId }) => {
  const { tokenRef } = useAuthRefresh(token, {
    backendUrl: BACKEND_URL,
    apiPrefix: API_PREFIX,
  });

  const authCfg = useCallback(
    (extra = {}) => ({
      ...extra,
      headers: { ...(extra.headers || {}), Authorization: `Bearer ${tokenRef.current}` },
    }),
    [tokenRef],
  );

  const get = useCallback(
    async (path, params) => (await axios.get(`${API}/pulse${path}`, authCfg({ params }))).data,
    [authCfg],
  );
  const post = useCallback(
    async (path, body) => (await axios.post(`${API}/pulse${path}`, body || {}, authCfg())).data,
    [authCfg],
  );
  const put = useCallback(
    async (path, body) => (await axios.put(`${API}/pulse${path}`, body || {}, authCfg())).data,
    [authCfg],
  );
  const del = useCallback(
    async (path) => (await axios.delete(`${API}/pulse${path}`, authCfg())).data,
    [authCfg],
  );

  const api = useMemo(() => ({
    getOverview:      ()            => get('/overview'),
    listUsers:        ()            => get('/users'),
    listDevices:      ()            => get('/devices'),
    enrollDevice:     (body)        => post('/devices/enroll', body),
    reissueToken:     (id)          => post(`/devices/${id}/reissue-token`),
    revokeDevice:     (id)          => post(`/devices/${id}/revoke`),
    unrevokeDevice:   (id)          => post(`/devices/${id}/unrevoke`),
    deleteDevice:     (id)          => del(`/devices/${id}`),
    mapDevice:        (id, uid)     => post(`/devices/${id}/map`, { harmony_user_id: uid }),
    bulkMapDevices:   (items)       => post('/devices/bulk-map', { items }),
    getPresence:      ()            => get('/presence'),
    getDailyReport:   (params)      => get('/reports/daily', params),
    getDayDetail:     (params)      => get('/reports/day', params),
    getReconcile:     (params)      => get('/reconcile', params),
    getAnomalies:     (params)      => get('/anomalies', params),
    getSettings:      ()            => get('/settings'),
    saveSettings:     (body)        => put('/settings', body),
    listWifi:         (params)      => get('/wifi', params),
    backfillPowered:  ()            => post('/maintenance/backfill-powered'),
    // automatic rollout
    getEnrollKeyStatus: ()          => get('/enroll-key'),
    rotateEnrollKey:  ()            => post('/enroll-key/rotate'),
    disableEnrollKey: ()            => del('/enroll-key'),
    // daily summary + exemptions
    getDailySummary:  (params)      => get('/reports/summary', params),
    setExemption:     (body)        => post('/exemptions', body),
    clearExemption:   (uid, date)   => del(`/exemptions/${uid}/${date}`),
    getWeekly:        (params)      => get('/reports/weekly', params),
    getWorkplace:     (params)      => get('/reports/workplace', params),
    getBrowsing:      (params)      => get('/browsing', params),
    runBrowsingCheck: ()            => post('/alerts/run-browsing'),
    getAfterHours:    (params)      => get('/reports/afterhours', params),
    getResourceReport: (params)     => get('/reports/resource', params),
    getHealthReport:  (params)      => get('/reports/health', params),
    runWeekly:        (params)      => post(`/alerts/run-weekly?end=${params.end}&days=${params.days}`),
    // work windows (shifts)
    getShifts:        ()            => get('/shifts'),
    assignShifts:     (items)       => put('/shifts/assign', { items }),
    // access management (Pulse-owned)
    listAccess:       ()            => get('/access'),
    setAccess:        (uid, body)   => put(`/access/${uid}`, body),
    removeAccess:     (uid)         => del(`/access/${uid}`),
    // alerting + governance
    listAlerts:       ()            => get('/alerts'),
    runDigest:        (date)        => post(`/alerts/run-digest${date ? `?date=${date}` : ''}`),
    runSilentCheck:   ()            => post('/alerts/run-silent-check'),
    runInactivityCheck: ()          => post('/alerts/run-inactivity'),
    runHealthCheck:   ()            => post('/alerts/run-health'),
    listAudit:        (params)      => get('/audit', params),
    listIngestErrors: ()            => get('/ingest-errors'),
  }), [get, post, put, del]);

  // Admin-authed download of an agent script as a file.
  const downloadAgentFile = useCallback(async (name) => {
    const r = await axios.get(`${API}/pulse/agent/download/${name}`, authCfg({ responseType: 'blob' }));
    const url = URL.createObjectURL(new Blob([r.data], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [authCfg]);

  // Admin status + access come from the backend /overview probe, not the role
  // prop — the backend treats timesheet_role === 'admin' as admin too, so
  // deriving it from `role` alone would be wrong. null = "not yet known".
  const [isAdmin, setIsAdmin] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [displayTz, setDisplayTz] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await get('/overview');
        if (cancelled) return;
        setIsAdmin(!!d.is_admin);
        const tz = d.settings?.display_timezone || null;
        setDisplayTimezone(tz);   // module-level, used by all formatters
        setDisplayTz(tz);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setBootError('forbidden');
        } else {
          setBootError('error');
        }
        setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [get]);

  const value = useMemo(
    () => ({ ...api, downloadAgentFile, backendUrl: BACKEND_URL, role, userName, userId, isAdmin, bootError, displayTz, tokenRef }),
    [api, downloadAgentFile, role, userName, userId, isAdmin, bootError, displayTz, tokenRef],
  );

  return <PulseContext.Provider value={value}>{children}</PulseContext.Provider>;
};

export default PulseContext;
